// Nederlandse teksten voor het documents-deel. Dezelfde sleutels als documents.en.ts;
// de typecheck faalt als er een ontbreekt of bijkomt.
//
// Let op: nergens "geboekt" waar het Engels het niet ook zegt. Goedkeuren zet
// een opdracht in de wachtrij; alleen een ontvangstbewijs bevestigt een boeking.

import type { Message, Vars } from './en.ts';
import type { documentsEn } from './documents.en.ts';

const n = (vars: Vars, name = 'count'): number => Number(vars[name] ?? 0);

export const documentsNl: Readonly<Record<keyof typeof documentsEn, Message>> = {
  'doc.channel.upload': 'geüpload',
  'doc.channel.mailbox': 'per e-mail',
  // --- het scherm en de inbox ------------------------------------------------
  'doc.title': 'Documenten',
  'doc.subtitle': 'Van ontvangst tot bevestigde boeking. Elke bron blijft herleidbaar.',
  'doc.replay.button': 'Bekijk het lezen opnieuw',
  'doc.inbox.subtitle': 'Documenten waar nog iets mee moet',
  'doc.inbox.error': 'De inbox met documenten kon niet worden gelezen.',
  'doc.inbox.emptyTitle': 'Er wacht niets',
  'doc.inbox.emptyText': 'Geen openstaande documenten in deze selectie.',
  'doc.kind.photo': 'Foto',
  'doc.loading': 'Laden…',
  'doc.select.title': 'Kies een document',
  'doc.select.text': 'De bron, het voorbereide voorstel en alles wat erover is besloten, verschijnen hier.',

  // --- het document ----------------------------------------------------------
  'doc.error.absent': 'Dit document is niet beschikbaar in jouw sessie.',
  'doc.error.read': 'Het document kon niet worden gelezen.',
  'doc.loadingDocument': 'Document laden…',
  'doc.header.received': (v: Vars) => `ontvangen ${v['when']}`,
  'doc.header.pages': (v: Vars) => `${n(v)} ${n(v) === 1 ? 'pagina' : 'pagina’s'}`,
  'doc.size.bytes': (v: Vars) => `${v['size']} bytes`,
  'doc.size.kb': (v: Vars) => `${v['size']} kB`,
  'doc.size.mb': (v: Vars) => `${v['size']} MB`,
  'doc.header.regionWaiting': 'Wacht op het regiorapport',
  'doc.header.reading': 'Je AI is aan het lezen',
  'doc.type.invoice': 'factuur',
  'doc.type.credit_note': 'creditnota',
  'doc.type.receipt': 'kassabon',
  'doc.direction.inbound': 'inkomend',
  'doc.direction.outbound': 'uitgaand',

  // --- de herhaling ----------------------------------------------------------
  'doc.replay.title': 'Herhaling van hoe dit is gelezen',
  'doc.replay.stored': 'Afgespeeld uit het opgeslagen verslag.',
  'doc.replay.took': (v: Vars) =>
    `Afgespeeld uit het opgeslagen verslag. Het lezen zelf duurde ${v['took']}, op ${v['when']}.`,
  'doc.replay.under': 'minder dan 0,1 s',

  // --- meldingen -------------------------------------------------------------
  'doc.statement.title': 'Bankafschrift',
  'doc.blockers.title': 'Gestopt voordat de voorbereiding klaar was',
  'doc.warnings.title': 'Controleer dit voordat je goedkeurt',
  'doc.vat.didYouMean': (v: Vars) => `Bedoel je ${v['candidate']}?`,
  'doc.vat.explain': (v: Vars) =>
    `Gelezen als ${v['read']}: de letter op de twaalfde positie hoort een B te zijn. Daarmee is het nummer precies dat van ${v['relation']}${v['code']} in e-Boekhouden. Er verandert niets tenzij je het bevestigt; je bevestiging wordt vastgelegd.`,
  'doc.vat.anyRelation': 'een bestaande relatie',
  'doc.vat.use': (v: Vars) => `${v['candidate']} gebruiken`,
  'doc.payment.title': 'Hoe is deze kassabon betaald?',
  'doc.payment.text': 'Dat staat niet op de kassabon. Hij wordt geboekt vanaf waar het geld vandaan kwam; je antwoord wordt vastgelegd.',
  'doc.payment.cash': 'Contant (Kas)',
  'doc.payment.bank': 'Pin of bank',
  'doc.payment.submit': 'Vastleggen en opnieuw lezen',
  'doc.settlement.state': (v: Vars) => `Verrekening met de oorspronkelijke factuur: ${v['state']}.`,
  'doc.settlement.offerTitle': (v: Vars) => `Verrekenen met factuur ${v['number']}`,
  'doc.settlement.offerText': (v: Vars) =>
    `${v['amount']} van deze creditnota tegen de oorspronkelijke factuur in e-Boekhouden. Er gaat geen geld heen en weer; de creditnota wordt weggestreept en de factuur verlaagd. Een aparte beslissing.`,
  'doc.settlement.approve': 'Verrekening goedkeuren',
  'doc.settlement.not_booked': 'Verrekenen met het origineel wordt aangeboden zodra deze creditnota is geboekt.',
  'doc.settlement.no_original': 'De creditnota noemt geen oorspronkelijke factuur, dus er is hier niets om mee te verrekenen.',
  'doc.settlement.not_released': 'Creditnota’s verrekenen in e-Boekhouden kan hier nog niet.',
  'doc.settlement.ledger_not_configured':
    'Voor deze administratie is geen verrekenrekening ingesteld, dus de verrekening kan nog niet worden goedgekeurd. Vraag een beheerder om die in te stellen.',
  'doc.region.pendingTitle': 'Wacht tot de provider meldt waar dit is verwerkt',
  'doc.region.neverTitle': 'De regio is nooit gemeld',
  'doc.region.held': 'Het model heeft geantwoord, maar het antwoord wordt vastgehouden en niet gebruikt totdat OpenRouter bevestigt dat het in de EU draaide.',
  'doc.region.asked': (v: Vars) => `${n(v)} keer gevraagd, laatst op ${v['at']}.`,
  'doc.region.next': (v: Vars) =>
    `Volgende controle ${v['next']}; komt het antwoord niet, dan neemt een persoon het op ${v['deadline']} over.`,
  'doc.region.askOnce': 'Je kunt het nog één keer vragen. Er wordt niets opnieuw verstuurd.',
  'doc.region.button': 'Regio opnieuw controleren',

  // --- de controles ----------------------------------------------------------
  'doc.checks.title': 'Controles',
  'doc.check.vat_arithmetic': 'Btw-berekening',
  'doc.check.vat_rate_code': 'Btw-tarief past bij de code',
  'doc.check.supplier_mapped': 'Leverancier gekoppeld',
  'doc.check.not_duplicate': 'Geen dubbele',
  'doc.check.region_confirmed': 'Regio bevestigd',
  'doc.check.outcome.passed': 'in orde',
  'doc.check.outcome.failed': 'niet in orde',
  'doc.check.outcome.not_run': 'nog niet uitgevoerd',
  'doc.check.beforeSending': 'wordt vlak voor verzending gecontroleerd',
  'doc.check.when.fields': 'Wordt uitgevoerd zodra elk verplicht veld is gelezen.',
  'doc.check.when.duplicate': 'Wordt bij het boekhoudpakket uitgevoerd wanneer de goedgekeurde boeking wordt verstuurd.',
  'doc.check.when.region': 'Wordt uitgevoerd wanneer de tekst naar het model gaat; bij deze lezing is niets opgevraagd.',

  // --- het boekingsvoorstel --------------------------------------------------
  'doc.proposal.none': 'Voor dit document is geen voorstel voorbereid.',
  'doc.proposal.title': 'Boekingsvoorstel',
  'doc.proposal.revision': (v: Vars) => `Versie ${v['revision']}`,
  'doc.proposal.booksAs': 'Boekt als',
  'doc.proposal.ledgers': 'Grootboekrekeningen',
  'doc.proposal.revenueLedger': 'Omzetrekening',
  'doc.proposal.costLedger': 'Kostenrekening',
  'doc.proposal.paidFrom': 'Betaald vanaf',
  'doc.proposal.vatCode': 'Btw-code',
  'doc.proposal.supplier': 'Leverancier',
  'doc.proposal.total': 'Totaal',
  'doc.proposal.vatNotReclaimed': (v: Vars) => `Btw niet teruggevraagd: ${v['amount']}.`,
  'doc.proposal.vatNotReclaimedWhy':
    'De btw van deze leverancier is niet aftrekbaar (dat zegt de regel voor deze leverancier), dus het brutobedrag komt op de kostenrekening, onder de code zonder btw.',
  'doc.booksAs.purchase.invoice': 'Inkoopfactuur',
  'doc.booksAs.purchase.credit_note': 'Inkoopcreditnota',
  'doc.booksAs.purchase.receipt': 'Kassabon (inkoop)',
  'doc.booksAs.purchase.document': 'Inkoopdocument',
  'doc.booksAs.purchase.other': (v: Vars) => `Inkoop (${v['kind']})`,
  'doc.booksAs.sales.invoice': 'Verkoopfactuur',
  'doc.booksAs.sales.credit_note': 'Verkoopcreditnota',
  'doc.booksAs.sales.receipt': 'Kassabon (verkoop)',
  'doc.booksAs.sales.document': 'Verkoopdocument',
  'doc.booksAs.sales.other': (v: Vars) => `Verkoop (${v['kind']})`,
  'doc.booksAs.none.invoice': 'Factuur',
  'doc.booksAs.none.credit_note': 'Creditnota',
  'doc.booksAs.none.receipt': 'Kassabon',
  'doc.ledger.code': (v: Vars) => `Grootboekrekening ${v['code']}`,
  'doc.ledger.ref': (v: Vars) => `Grootboekrekening ref. ${v['ref']} (nog niet gesynchroniseerd)`,

  // --- waar en hoe het is gelezen --------------------------------------------
  'doc.route.readBy': (v: Vars) => `Gelezen door ${v['model']}${v['host'] === '' ? '' : ` op ${v['host']}`}`,
  'doc.route.unrecordedModel': 'een niet vastgelegd model',
  'doc.route.confirmed': 'regio bevestigd door de provider',
  'doc.route.notConfirmed': 'regio NIET bevestigd',
  'doc.details.summary': 'Bron- en leesgegevens',
  'doc.details.direction': 'Richting',
  'doc.details.type': 'Soort',
  'doc.details.notDetermined': 'Niet bepaald',
  'doc.details.hash': 'Inhoudshash',
  'doc.details.versions': 'Versies',
  'doc.details.noVersions': 'Geen opgeslagen',
  'doc.details.readAs': 'Gelezen als',
  'doc.details.notRecorded': 'Niet vastgelegd',
  'doc.details.pages': 'Pagina’s',
  'doc.details.unreadPages': (v: Vars) =>
    n(v) === 1
      ? `pagina ${v['pages']} had geen tekst en is niet gelezen`
      : `pagina’s ${v['pages']} hadden geen tekst en zijn niet gelezen`,
  'doc.details.ocr': 'OCR-zekerheid',
  'doc.details.modelRoute': 'Modelroute',
  'doc.details.converted': 'Omgezet',
  'doc.details.convertedIntro': 'Gelezen in Nederlandse notatie en omgezet vóór de controle — vergelijk met het papier:',
  'doc.details.dayFirst': '(dag eerst gelezen)',
  'doc.details.line': (v: Vars) => `Regel ${v['number']} `,
  'doc.details.vatRow': (v: Vars) => `Btw-regel ${v['number']} `,
  'doc.details.regionCheck': 'Regiocontrole',
  'doc.details.lookups': (v: Vars) => `${n(v)} ${n(v) === 1 ? 'opvraging' : 'opvragingen'}`,
  'doc.details.lastHttp': (v: Vars) => `, laatste HTTP ${v['status']}`,
  'doc.routeCheck.reported': 'Bevestigd door de provider',
  'doc.routeCheck.not_found': 'Niet bevestigd — de provider had niet op tijd vastgelegd waar het draaide; het antwoord is weggegooid',
  'doc.routeCheck.incomplete': 'Niet bevestigd — in de vastlegging van de provider stond nooit de regio; het antwoord is weggegooid',
  'doc.routeCheck.http_error': 'Niet bevestigd — de provider weigerde de opvraging; het antwoord is weggegooid',
  'doc.routeCheck.network': 'Niet bevestigd — de provider was niet bereikbaar voor de opvraging; het antwoord is weggegooid',
  'doc.routeCheck.not_json': 'Niet bevestigd — het antwoord van de provider op de opvraging was onleesbaar; het antwoord is weggegooid',

  // --- een bankafschrift -----------------------------------------------------
  'doc.stmt.notImported': 'Niet geïmporteerd. De reden staat hieronder; er is geen regel uit dit bestand opgeslagen.',
  'doc.stmt.lines': 'Regels',
  'doc.stmt.lineCounts': (v: Vars) => `${v['count']} in het bestand · ${v['new']} nieuw`,
  'doc.stmt.alreadyStored': (v: Vars) => `${v['count']} al opgeslagen uit een eerder bestand`,
  'doc.stmt.candidates': 'Matchkandidaten',
  'doc.stmt.noneFound': 'Geen gevonden',
  'doc.stmt.offered': (v: Vars) => `${v['count']} aangeboden — bekijk ze bij Bankafletteren`,
  'doc.stmt.openItems': 'Openstaande posten gecontroleerd',
  'doc.stmt.notChecked': 'Niet gecontroleerd',
  'doc.stmt.account': 'Rekening',
  'doc.stmt.sequence': (v: Vars) => `afschrift ${v['sequence']}`,
  'doc.stmt.period': 'Periode',
  'doc.stmt.balance': 'Saldo',
  'doc.stmt.provedLines': (v: Vars) => `${v['count']} regels, sluitend bevonden`,

  // --- goedkeuren en uitsluiten ----------------------------------------------
  'doc.actions.excluded': 'Uitgesloten van de verwerking. Er is niets verwijderd in het boekhoudpakket.',
  'doc.actions.explain':
    'Goedkeuren legt je beslissing vast en zet één opdracht in de wachtrij. Er wordt niets verstuurd totdat de verwerker die oppakt.',
  'doc.actions.exclude': 'Uitsluiten…',
  'doc.actions.approve': 'Goedkeuren',
  'doc.actions.submitting': 'Bezig met indienen…',
  'doc.actions.reason': 'Reden',
  'doc.actions.reasonPlaceholder': 'Waarom dit document wordt uitgesloten',
  'doc.actions.evidence': 'Bewijsverwijzing',
  'doc.actions.evidencePlaceholder': 'bijv. document:INV-2026-0912',
  'doc.actions.excludeDocument': 'Document uitsluiten',
  'doc.actions.noRevision': 'Dit voorstel heeft geen actuele versie om goed te keuren.',
  'doc.actions.failed': 'Het verzoek kon niet worden uitgevoerd.',
  'doc.actions.stale':
    'Dit is veranderd sinds het scherm werd geladen, dus er is niets ingediend. De nieuwste versie staat nu op het scherm.',
  'doc.actions.forbidden': 'Jouw rol mag dit niet doen.',
  'doc.actions.absent': 'Dit is niet meer beschikbaar in jouw sessie.',
  'doc.actions.refused': (v: Vars) => `Het verzoek is geweigerd: ${v['fields']}.`,
  'doc.actions.validation': 'controle mislukt',
  'doc.external.stillRunning':
    'Nog niet afgerond. De opdracht loopt op de achtergrond door — open dit document later opnieuw om te zien hoe ver hij is.',

  // --- de verwerkingsgeschiedenis --------------------------------------------
  'doc.history.title': 'Verwerkingsgeschiedenis',
  'doc.history.system': 'het systeem',
  'doc.history.you': 'jou',
  'doc.history.colleague': 'een collega',
  'doc.history.received': 'Document ontvangen',
  'doc.history.version': (v: Vars) => `Bronversie ${v['version']} opgeslagen`,
  'doc.history.proposal': 'voorstel voorbereid',
  'doc.history.stopped': (v: Vars) => `gestopt voor een persoon (${n(v)} ${n(v) === 1 ? 'blokkade' : 'blokkades'})`,
  'doc.history.unreadable': 'kon niet worden gelezen',
  'doc.history.regionLate': 'Regio niet op tijd gemeld',
  'doc.history.regionConfirmed': 'Regio bevestigd — het eerdere antwoord gebruikt, niets opnieuw verstuurd',
  'doc.history.readAgain': 'Opnieuw gelezen',
  'doc.history.read': 'Gelezen',
  'doc.history.settlementConfirmed': (v: Vars) => `Verrekening bevestigd, mutatie ${v['mutation']}`,
  'doc.history.ruleSaved': (v: Vars) => `Leveranciersregel opgeslagen door ${v['who']}`,
  'doc.history.relation': (v: Vars) => `relatie ${v['relation']}`,
  'doc.history.vatNotDeductible': 'Btw niet aftrekbaar',
  'doc.history.classified': (v: Vars) => `Tegenpartij ingedeeld door ${v['who']}`,
  'doc.history.one_off': 'een eenmalige aankoop',
  'doc.history.recurring_business': 'een vaste zakelijke leverancier',
  'doc.history.by': (v: Vars) => `${v['title']} door ${v['who']}`,
  'doc.history.approve_submission': 'Goedgekeurd',
  'doc.history.reject': 'Afgewezen',
  'doc.history.exclude': 'Uitgesloten',
  'doc.history.verify_outcome': 'Uitkomst geverifieerd',
  'doc.history.approve_settlement': 'Verrekening goedgekeurd',
  'doc.history.decision': (v: Vars) => `Beslissing · ${v['kind']}`,
};
